import { Component, type ReactNode } from 'react';

interface Props { children: ReactNode }
interface State { error: Error | null }

/**
 * Catches render errors so a crash shows a readable message instead of a blank
 * white screen (the SPA otherwise unmounts entirely on an uncaught error).
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error) {
    // Surface in the console for diagnosis.
    console.error('Unhandled UI error:', error);
  }

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div className="mx-auto max-w-2xl p-8">
        <div className="rounded-lg border border-border bg-card p-6 shadow-sm">
          <h1 className="mb-2 text-lg font-semibold text-primary">Something went wrong</h1>
          <p className="mb-4 text-sm text-muted-foreground">
            This screen hit an unexpected error. Try going back or reloading. If it
            persists, share the details below.
          </p>
          <pre className="max-h-64 overflow-auto rounded bg-muted/50 p-3 text-xs text-foreground">
            {this.state.error.message}
          </pre>
          <div className="mt-4 flex gap-3">
            <button className="btn-secondary" onClick={() => history.back()}>Back</button>
            <button className="btn-primary" onClick={() => location.reload()}>Reload</button>
          </div>
        </div>
      </div>
    );
  }
}
